import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, setToken, getToken, type MeResponse } from '../api/client';

interface SessionUser {
  id: string;
  email: string;
  displayName?: string;
}

interface SessionState {
  loading: boolean;
  user: SessionUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<SessionUser | null>(null);

  async function fetchMe(): Promise<void> {
    const token = getToken();
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const me: MeResponse = await api.me();
      setUser(me.user ? { id: me.user.id, email: me.user.email, displayName: me.user.displayName } : null);
    } catch {
      // Token invalid — api/client clears it; we render unauth state.
      setUser(null);
    }
  }

  useEffect(() => {
    fetchMe().finally(() => setLoading(false));
    // Listen for the api client's session-expired event (fires on any 401).
    const onExpired = (): void => {
      setUser(null);
    };
    window.addEventListener('sitekit:session-expired', onExpired);
    return () => window.removeEventListener('sitekit:session-expired', onExpired);
  }, []);

  async function login(email: string, password: string): Promise<void> {
    const r = await api.login(email, password);
    setToken(r.token);
    setUser({ id: r.user.id, email: r.user.email, displayName: r.user.displayName });
  }

  async function logout(): Promise<void> {
    try { await api.logout(); } catch { /* ignore — clearing client-side regardless */ }
    setToken(null);
    setUser(null);
  }

  return (
    <Ctx.Provider value={{ loading, user, login, logout, refresh: fetchMe }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used inside <SessionProvider>');
  return v;
}
